<!-- Tier 1: views and actions (design handoff, "Step 4, streamlined", section 2). -->
<div class="page-header-tier page-header-tier-1" *ngIf="hasTier1()">
  <div class="page-views" *ngIf="views()?.length">
    <button
      type="button"
      class="page-view"
      *ngFor="let view of views(); trackBy: trackByKey"
      [class.page-view-active]="view.key === activeView()"
      (click)="selectView(view)"
    >
      {{ view.label | translate }}
      <span
        class="page-view-count"
        *ngIf="view.count !== null && view.count !== undefined"
        >{{ view.count }}</span
      >
    </button>
  </div>

  <div class="page-actions">
    <sh-action-bar
      [primaryAction]="primaryAction()"
      [secondaryActions]="secondaryActions()"
      [overflowActions]="overflowActions()"
    ></sh-action-bar>
  </div>
</div>

<!-- Tier 2: search and filters. -->
<div class="page-header-tier page-header-tier-2" *ngIf="hasTier2()">
  <sh-search-input
    *ngIf="showSearch()"
    class="page-search"
    [value]="searchText()"
    (valueChange)="onSearchInput($event ?? '')"
    [placeholder]="searchPlaceholder() ?? 'shared.terms.search'"
  ></sh-search-input>

  <div
    class="page-filters-divider"
    *ngIf="showSearch() && filters()?.length"
    [style.visibility]="filtersWrapped() ? 'hidden' : null"
  ></div>

  <div class="page-filters" *ngIf="filters()?.length">
    <button
      type="button"
      class="filter-link"
      *ngFor="let filter of linkFilters(); trackBy: trackByKey"
      [attr.data-filter-key]="filter.key"
      (click)="openFilterPopover($event, filter)"
    >
      <span class="filter-link-field">{{ filter.label | translate }}</span>
      <span class="filter-link-value">{{ filterValueText(filter) }}</span>
    </button>

    <button
      type="button"
      class="filter-add"
      *ngIf="hasInactiveFilters()"
      (click)="openAddFilterList($event)"
    >
      + {{ 'shell.pageHeader.addFilter' | translate }}
    </button>

    <button
      type="button"
      class="filter-clear-all"
      *ngIf="hasActiveFilters()"
      (click)="clearAllFilters()"
    >
      {{ 'shell.pageHeader.clearAll' | translate }}
    </button>
  </div>

  <div class="page-header-tier-2-right">
    <span
      class="page-results"
      *ngIf="resultCount() !== null && resultCount() !== undefined"
    >
      <ng-container
        *ngIf="
          totalCount() !== null && totalCount() !== undefined;
          else resultsOnly
        "
      >
        {{
          'shell.pageHeader.resultsOfTotal'
            | translate: { count: resultCount(), total: totalCount() }
        }}
      </ng-container>
      <ng-template #resultsOnly>
        {{ 'shell.pageHeader.results' | translate: { count: resultCount() } }}
      </ng-template>
    </span>

    <button
      type="button"
      class="filter-all-link"
      *ngIf="filters()?.length"
      (click)="openFiltersDrawer()"
    >
      {{
        'shell.pageHeader.allFilters'
          | translate: { count: activeFilters().length }
      }}
    </button>
  </div>
</div>

<!-- Shared popover for one filter's control, anchored to whichever filter link/add-filter item was clicked. -->
<sh-popover
  #filterPopover
  styleClass="page-filter-popover"
  (closed)="onFilterPopoverClosed()"
>
  <ng-container *ngIf="activeFilter() as filter">
    <sh-field [label]="filter.label" [ngSwitch]="filter.type">
      <sh-select
        *ngSwitchCase="'select'"
        [options]="filter.options"
        optionLabel="label"
        optionValue="value"
        appendTo="self"
        [value]="filter.value"
        (valueChange)="setPopoverFilterValue(filter, $event)"
      ></sh-select>

      <sh-multiselect
        *ngSwitchCase="'multiselect'"
        [options]="filter.options"
        optionLabel="label"
        optionValue="value"
        appendTo="self"
        [value]="$any(filter.value)"
        (valueChange)="setPopoverFilterValue(filter, $event)"
      ></sh-multiselect>

      <sh-input
        *ngSwitchCase="'text'"
        [value]="$any(filter.value)"
        (valueChange)="onTextFilterInput(filter, $event)"
      ></sh-input>

      <div *ngSwitchCase="'boolean'" class="filter-boolean-control">
        <sh-toggle
          [value]="!!filter.value"
          (valueChange)="setPopoverFilterValue(filter, $event)"
          [ariaLabel]="filter.label"
        ></sh-toggle>
        <span>{{
          (filter.value ? 'shared.terms.yes' : 'shared.terms.no') | translate
        }}</span>
      </div>

      <sh-date-picker
        *ngSwitchCase="'date'"
        valueType="date"
        appendTo="self"
        [value]="$any(filter.value)"
        (valueChange)="setPopoverFilterValue(filter, $event)"
      ></sh-date-picker>

      <sh-date-picker
        *ngSwitchCase="'dateRange'"
        selectionMode="range"
        appendTo="self"
        [value]="$any(filter.value)"
        (valueChange)="setPopoverFilterValue(filter, $event)"
      ></sh-date-picker>
    </sh-field>

    <button type="button" class="filter-remove" (click)="clearFilter(filter)">
      {{ 'shared.terms.remove' | translate }}
    </button>
  </ng-container>
</sh-popover>

<!-- "+ Add filter" list popover. -->
<sh-popover #addFilterPopover styleClass="page-add-filter-popover">
  <div class="add-filter-list">
    <button
      type="button"
      class="add-filter-list-item"
      *ngFor="let filter of addFilterList; trackBy: trackByKey"
      (click)="pickAddFilter($event, filter)"
    >
      {{ filter.label | translate }}
    </button>
  </div>
</sh-popover>

<!-- "All filters (n)" drawer. -->
<sh-drawer
  position="right"
  styleClass="page-filters-drawer"
  title="shell.pageHeader.allFiltersTitle"
  [visible]="drawerOpen()"
  (visibleChange)="drawerOpen.set($event)"
>
  <div class="page-filters-drawer-body">
    <sh-field
      *ngFor="let filter of workingFilters(); trackBy: trackByKey"
      [label]="filter.label"
      [ngSwitch]="filter.type"
    >
      <sh-select
        *ngSwitchCase="'select'"
        [options]="filter.options"
        optionLabel="label"
        optionValue="value"
        appendTo="self"
        [value]="filter.value"
        (valueChange)="setFilterValue(filter, $event)"
      ></sh-select>

      <sh-multiselect
        *ngSwitchCase="'multiselect'"
        [options]="filter.options"
        optionLabel="label"
        optionValue="value"
        appendTo="self"
        [value]="$any(filter.value)"
        (valueChange)="setFilterValue(filter, $event)"
      ></sh-multiselect>

      <sh-input
        *ngSwitchCase="'text'"
        [value]="$any(filter.value)"
        (valueChange)="onTextFilterInput(filter, $event)"
      ></sh-input>

      <div *ngSwitchCase="'boolean'" class="filter-boolean-control">
        <sh-toggle
          [value]="!!filter.value"
          (valueChange)="setFilterValue(filter, $event)"
          [ariaLabel]="filter.label"
        ></sh-toggle>
        <span>{{
          (filter.value ? 'shared.terms.yes' : 'shared.terms.no') | translate
        }}</span>
      </div>

      <sh-date-picker
        *ngSwitchCase="'date'"
        valueType="date"
        appendTo="self"
        [value]="$any(filter.value)"
        (valueChange)="setFilterValue(filter, $event)"
      ></sh-date-picker>

      <sh-date-picker
        *ngSwitchCase="'dateRange'"
        selectionMode="range"
        appendTo="self"
        [value]="$any(filter.value)"
        (valueChange)="setFilterValue(filter, $event)"
      ></sh-date-picker>
    </sh-field>
  </div>

  <ng-template shTemplate="footer">
    <button type="button" class="filter-clear-all" (click)="clearAllFilters()">
      {{ 'shell.pageHeader.clearAll' | translate }}
    </button>
  </ng-template>
</sh-drawer>
